'use client';
import { useCallback, useState } from 'react';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { alpha, Box, Typography } from '@mui/material';

import { useT } from '@/hooks';
import type { EventMarket, EventResponse, OutcomeItem } from '@/types';
import { resolveOutcomeData } from '@/utils/marketUtils';
import { OutcomeCard } from './OutcomeCard';
import { ResolvedOutcomeRow } from './ResolvedOutcomeRow';

interface OutcomeCardListProps {
  event: EventResponse;
  outcomes: OutcomeItem[];
  resolvedOutcomes?: OutcomeItem[];
  selectedOutcomeId: string | null;
  effectiveSelectedOutcomeId?: string | null;
  selectedSide?: 'yes' | 'no';
  activeTab?: 'buy' | 'sell';
  onOutcomeClick: (outcomeId: string) => () => void;
  onBuyYesClick: (outcomeId: string) => () => void;
  onBuyNoClick: (outcomeId: string) => () => void;
}

export function OutcomeCardList({
  event,
  outcomes,
  resolvedOutcomes = [],
  selectedOutcomeId,
  effectiveSelectedOutcomeId,
  selectedSide,
  activeTab,
  onOutcomeClick,
  onBuyYesClick,
  onBuyNoClick,
}: OutcomeCardListProps) {
  const [expandedOutcomeId, setExpandedOutcomeId] = useState<string | null>(null);
  const [showResolved, setShowResolved] = useState(false);
  const { t } = useT();

  function handleExpand(outcomeId: string) {
    setExpandedOutcomeId((prev) => (prev === outcomeId ? null : outcomeId));
    if (onOutcomeClick) {
      onOutcomeClick(outcomeId)();
    }
  }

  const toggleResolved = useCallback(() => {
    setShowResolved((prev) => !prev);
  }, []);

  function renderOutcomeCard(item: OutcomeItem) {
    const { id: outcomeId } = resolveOutcomeData(item);

    return (
      <OutcomeCard
        key={item.id}
        data={item}
        isSelected={effectiveSelectedOutcomeId === outcomeId}
        selectedSide={effectiveSelectedOutcomeId === outcomeId ? selectedSide : undefined}
        isExpanded={expandedOutcomeId === outcomeId}
        activeTab={activeTab}
        onExpand={handleExpand}
        onBuyYesClick={onBuyYesClick(outcomeId)}
        onBuyNoClick={onBuyNoClick(outcomeId)}
      />
    );
  }

  const sortedOutcomes = outcomes
    .filter((item) => {
      const data = resolveOutcomeData(item);
      return data.active && !data.archived;
    })
    .sort((a, b) => {
      const { price: priceA } = resolveOutcomeData(a);
      const { price: priceB } = resolveOutcomeData(b);
      return priceB - priceA;
    });

  const outcomeItems = sortedOutcomes.map(renderOutcomeCard);

  const renderResolvedItems = (m: OutcomeItem) => {
    const firstOutcomeId = Array.isArray(m.outcomes) ? m.outcomes[0]?.id : null;
    const isSelected = effectiveSelectedOutcomeId === firstOutcomeId;
    return (
      <ResolvedOutcomeRow
        key={m.id}
        event={event}
        market={m as EventMarket}
        isSelected={isSelected}
        onSelect={firstOutcomeId ? onOutcomeClick(firstOutcomeId) : undefined}
      />
    );
  };
  const renderResolvedItemsList = resolvedOutcomes
    .filter((item) => !resolveOutcomeData(item).archived)
    .map(renderResolvedItems);

  return (
    <>
      {/* Mobile Header Row */}
      <Box
        sx={{
          display: { xs: 'flex', md: 'none' },
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1.5,
          mb: 1.5,
        }}
      >
        <Typography
          sx={{
            fontSize: '0.875rem',
            fontWeight: 700,
            color: 'text.secondary',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
          }}
        >
          {t('portfolio.openOrders.colMarket')}
        </Typography>
        {sortedOutcomes.length > 0 && (
          <Box
            sx={{
              display: { xs: 'flex', md: 'none' },
              alignItems: 'center',
              justifyContent: 'end',
              gap: 0.75,
            }}
          >
            <Typography
              sx={{
                fontSize: '0.875rem',
                fontWeight: 700,
                color: 'text.secondary',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
              }}
            >
              {t('market.percentChance')}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Mobile Cards Container */}
      <Box
        sx={{
          display: { xs: 'flex', md: 'none' },
          flexDirection: 'column',
          gap: 0,
        }}
      >
        {!showResolved && outcomeItems}

        {outcomeItems &&
          outcomeItems.length > 0 &&
          renderResolvedItemsList &&
          renderResolvedItemsList.length > 0 && (
            <>
              <Box
                onClick={toggleResolved}
                sx={{
                  mt: 2,
                  mb: 1,
                  px: 2,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  cursor: 'pointer',
                  '&:hover': { opacity: 0.8 },
                }}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  fontWeight={700}
                  sx={{
                    fontSize: '0.8125rem',
                    letterSpacing: '0.02em',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                  }}
                >
                  {showResolved ? t('market.hideResolved') : t('market.viewResolved')}
                  {showResolved ? (
                    <ExpandLessIcon sx={{ fontSize: 18 }} />
                  ) : (
                    <ExpandMoreIcon sx={{ fontSize: 18 }} />
                  )}
                </Typography>
              </Box>
              {showResolved && renderResolvedItemsList}
            </>
          )}

        {outcomeItems &&
          outcomeItems.length <= 0 &&
          renderResolvedItemsList &&
          renderResolvedItemsList.length > 0 && <>{renderResolvedItemsList}</>}
      </Box>

      {/* Desktop Container */}
      <Box
        sx={{
          display: { xs: 'none', md: 'flex' },
          flexDirection: 'column',
          gap: 0,
          borderRadius: '12px',
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          overflow: 'hidden',
          maxHeight: '520px',
        }}
      >
        {/* Desktop Header Row */}
        <Box
          sx={{
            display: { xs: 'flex', md: 'flex' },
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2.5,
            py: 1.5,
            borderBottom: '1px solid',
            borderColor: 'divider',
            flexShrink: 0,
            bgcolor: 'background.paper',
            zIndex: 1,
          }}
        >
          {/* MARKET Label - Left aligned */}
          <Typography
            sx={{
              fontSize: '0.875rem',
              fontWeight: 700,
              color: 'text.secondary',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              minWidth: 200,
              flex: 1,
            }}
          >
            {t('portfolio.openOrders.colMarket')}
          </Typography>

          {sortedOutcomes.length > 0 && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'end',
                minWidth: 100,
                flex: 1,
              }}
            >
              <Typography
                sx={{
                  fontSize: '0.875rem',
                  fontWeight: 700,
                  color: 'text.secondary',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  mr: 6,
                }}
              >
                {t('market.percentChance')}
              </Typography>
            </Box>
          )}

          {/* Spacer for Buy/Sell buttons alignment */}
          {sortedOutcomes.length > 0 && <Box sx={{ minWidth: 260 }} />}
        </Box>

        {/* Scrollable Content */}
        {!showResolved && (
          <Box
            sx={{
              flex: 1,
              overflowY: 'auto',
              '&::-webkit-scrollbar': {
                width: '6px',
              },
              '&::-webkit-scrollbar-track': {
                background: 'transparent',
              },
              '&::-webkit-scrollbar-thumb': {
                background: (theme) => alpha(theme.palette.text.primary, 0.1),
                borderRadius: '3px',
              },
              '&:hover::-webkit-scrollbar-thumb': {
                background: (theme) => alpha(theme.palette.text.primary, 0.2),
              },
            }}
          >
            {outcomeItems}
          </Box>
        )}

        {outcomeItems &&
          outcomeItems.length > 0 &&
          renderResolvedItemsList &&
          renderResolvedItemsList.length > 0 && (
            <>
              <Box
                onClick={toggleResolved}
                sx={{
                  px: 2.5,
                  py: 1,
                  bgcolor: 'background.paper',
                  borderTop: '1px solid',
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  cursor: 'pointer',
                  position: 'sticky',
                  bottom: 0,
                  zIndex: 100,
                  '&:hover': {
                    backgroundImage: (theme) =>
                      `linear-gradient(${alpha(theme.palette.text.primary, 0.04)}, ${alpha(theme.palette.text.primary, 0.04)})`,
                  },
                }}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  fontWeight={700}
                  sx={{
                    fontSize: '0.8125rem',
                    letterSpacing: '0.02em',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                  }}
                >
                  {showResolved ? t('market.hideResolved') : t('market.viewResolved')}
                  {showResolved ? (
                    <ExpandLessIcon sx={{ fontSize: 18 }} />
                  ) : (
                    <ExpandMoreIcon sx={{ fontSize: 18 }} />
                  )}
                </Typography>
              </Box>
              {showResolved && (
                <Box
                  sx={{
                    flex: 1,
                    overflowY: 'auto',
                    '&::-webkit-scrollbar': {
                      width: '6px',
                    },
                    '&::-webkit-scrollbar-track': {
                      background: 'transparent',
                    },
                    '&::-webkit-scrollbar-thumb': {
                      background: (theme) => alpha(theme.palette.text.primary, 0.1),
                      borderRadius: '3px',
                    },
                    '&:hover::-webkit-scrollbar-thumb': {
                      background: (theme) => alpha(theme.palette.text.primary, 0.2),
                    },
                  }}
                >
                  {renderResolvedItemsList}
                </Box>
              )}
            </>
          )}
        {outcomeItems &&
          outcomeItems.length <= 0 &&
          renderResolvedItemsList &&
          renderResolvedItemsList.length > 0 && (
            <Box
              sx={{
                flex: 1,
                overflowY: 'auto',
                '&::-webkit-scrollbar': {
                  width: '6px',
                },
                '&::-webkit-scrollbar-track': {
                  background: 'transparent',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: (theme) => alpha(theme.palette.text.primary, 0.1),
                  borderRadius: '3px',
                },
                '&:hover::-webkit-scrollbar-thumb': {
                  background: (theme) => alpha(theme.palette.text.primary, 0.2),
                },
              }}
            >
              {renderResolvedItemsList}
            </Box>
          )}
      </Box>
    </>
  );
}

export default OutcomeCardList;
